import React, { ChangeEvent, forwardRef } from "react";
import { InputProps as MuiInputProps } from "@mui/material/Input";
import { WithTesting } from "../../types";
import StyledFormHelperText from "../FormHelperText/FormHelperText.styled";
import IconButton from "../IconButton/IconButton";
import InputAdornment from "../InputAdornment";
import InputLabel from "../InputLabel";
import { StyledText, StyledTextContainer } from "./Text.styled";
import Tooltip from "../Tooltip";

export type TextProps = MuiInputProps &
  WithTesting & {
    /**
     * The input label.
     */
    label?: string;
    /**
     * The error message that will be displayed. Displayed only if an error condition has already been transmitted.
     */
    errorMessage?: string;
    withoutClearIcon?: boolean;
    maxLength?: number;
    onClear?(): void;
    endAdornment?: React.ReactNode;
  };

const Text = forwardRef<HTMLInputElement, TextProps>(
  (
    {
      disabled,
      value,
      onChange,
      label,
      error,
      errorMessage,
      required,
      maxLength,
      dataTestId,
      withoutClearIcon = false,
      onClear,
      endAdornment,
      ...props
    },
    ref,
  ) => {
    const handleClear = () => {
      onChange?.({ target: { value: "" } } as ChangeEvent<HTMLInputElement>);
      onClear?.();
    };

    return (
      <StyledTextContainer>
        {label && (
          <InputLabel htmlFor={dataTestId} required={required}>
            {label}
          </InputLabel>
        )}
        <StyledText
          inputRef={ref}
          inputProps={{ "data-testid": dataTestId, maxLength }}
          id={dataTestId}
          value={value}
          disabled={disabled}
          onChange={onChange}
          error={error}
          disableUnderline
          endAdornment={
            <>
              {Boolean(endAdornment) && (
                <InputAdornment position="end">{endAdornment}</InputAdornment>
              )}
              {Boolean(!withoutClearIcon && !disabled && value) && (
                <InputAdornment position="end">
                  <Tooltip
                    title="Clear"
                    type="light"
                    slotProps={{
                      popper: {
                        modifiers: [
                          { name: "offset", options: { offset: [0, -10] } },
                        ],
                      },
                    }}
                  >
                    <IconButton
                      //TODO: Consult with designers what should be the IconButton type here and replace
                      type="UNSAFE_transparent"
                      shape="round"
                      name="close"
                      onClick={handleClear}
                    />
                  </Tooltip>
                </InputAdornment>
              )}
            </>
          }
          {...props}
        />
        {error && errorMessage && (
          <StyledFormHelperText>{errorMessage}</StyledFormHelperText>
        )}
      </StyledTextContainer>
    );
  },
);

export default Text;
